import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

/**
 * Ordem padrão da aba CONVERSA do painel de detalhes (#1243).
 *
 * A aba de contato tem lista própria (`DEFAULT_CONVERSATION_CONTACT_ITEMS_ORDER`)
 * e chave própria: misturar as duas foi exatamente o que a issue mandou desfazer.
 *
 * Os participantes fecham a lista, e não vêm logo depois dos campos: a coluna é
 * lida de cima para baixo enquanto se atende, e as três primeiras seções são as
 * que mudam durante o atendimento (etiquetas, responsável, campos). Fluxos,
 * conversas anteriores e participantes são consulta.
 *
 * PADRÃO, não posição fixa: as seis continuam arrastáveis, e quem já arrastou
 * mantém a ordem que escolheu — o padrão só vale para quem nunca mexeu. O botão
 * de restaurar ordenação devolve exatamente esta lista.
 */
export const DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER = Object.freeze([
  { name: 'conversation_labels' },
  { name: 'conversation_assignment' },
  { name: 'conversation_info' },
  { name: 'flows' },
  { name: 'previous_conversation' },
  { name: 'conversation_participants' },
]);

/**
 * Ordem padrão da aba CONTATO do painel de detalhes (#1243), gravada em
 * `conversation_contact_sidebar_items_order`.
 *
 * Chave NOVA, e não uma reutilização de `contact_sidebar_items_order`: aquela é
 * da página de contatos, tem outro conjunto de seções e quem arrastar uma não
 * deve reordenar a outra.
 */
export const DEFAULT_CONVERSATION_CONTACT_ITEMS_ORDER = Object.freeze([
  { name: 'contact_details' },
  { name: 'social_profiles' },
  { name: 'contact_labels' },
  { name: 'contact_assignment' },
  { name: 'contact_attributes' },
  { name: 'related_companies' },
]);

/**
 * As duas abas do painel de detalhes (#1243) e a que abre quando nao ha nada
 * gravado.
 *
 * O padrao e a CONVERSA mesmo ela sendo a segunda na barra: quem abre uma
 * conversa esta atras do que aconteceu nela, e o contato e consulta. A ordem
 * visual e assunto do `PanelTabs`, nao daqui.
 */
export const CONVERSATION_PANEL_TABS = Object.freeze([
  'conversation',
  'contact',
]);

export const DEFAULT_CONVERSATION_PANEL_TAB = 'conversation';

/**
 * Nomes de item da barra lateral renomeados, no formato
 * `nome antigo -> nome novo`.
 *
 * #1243: `conversation_actions` virou `conversation_assignment` — quem já
 * arrastou a seção de ações da conversa mantém a posição relativa dela na aba
 * nova. O `macros -> flows` saiu na #999 (medido em produção em 09/10/2026:
 * zero usuários com o nome antigo gravado).
 */
export const LEGACY_SIDEBAR_ITEM_NAMES = Object.freeze({
  conversation_actions: 'conversation_assignment',
});

/**
 * Ordem padrão das seções do painel de detalhes do CONTATO (lista de Contatos
 * e tela do contato), gravada em `contact_sidebar_items_order`.
 *
 * #1652: "Responsável" e "Empresas relacionadas" entraram na lista arrastável
 * (antes eram cartões fixos no topo). Elas ficam no TOPO, que era onde estavam:
 * quem já tinha a lista de três itens gravada recebe as duas novas ao lado do
 * vizinho de cima do padrão (`normalizeSidebarItemsOrder`), e portanto na mesma
 * posição visual de antes.
 */
export const DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER = Object.freeze([
  { name: 'contact_assignment' },
  { name: 'related_companies' },
  { name: 'contact_attributes' },
  { name: 'contact_labels' },
  { name: 'previous_conversation' },
]);

/**
 * Slugifies the channel name.
 * Replaces spaces, hyphens, and double colons with underscores.
 * @param {string} name - The channel name to slugify.
 * @returns {string} The slugified channel name.
 */
const slugifyChannel = name =>
  name?.toLowerCase().replace(' ', '_').replace('-', '_').replace('::', '_');

const normalizeSidebarItemsOrder = (itemsOrder, defaultItemsOrder) => {
  if (!Array.isArray(itemsOrder)) {
    return [...defaultItemsOrder];
  }

  const sanitizedItems = itemsOrder.filter(
    item => item && typeof item === 'object' && typeof item.name === 'string'
  );

  if (!sanitizedItems.length) {
    return [...defaultItemsOrder];
  }

  // A renomeação acontece ANTES do dedupe, não depois: quem já tiver os dois
  // nomes gravados (arrastou a seção depois do rollout, com o valor antigo
  // ainda no banco) mantém a PRIMEIRA posição, que é a que ele enxergava.
  const renamedItems = sanitizedItems.map(item =>
    LEGACY_SIDEBAR_ITEM_NAMES[item.name]
      ? { ...item, name: LEGACY_SIDEBAR_ITEM_NAMES[item.name] }
      : item
  );

  // O descarte vem DEPOIS da renomeação (senão `macros` e `conversation_actions`
  // seriam jogados fora antes de virarem nome válido) e ANTES do dedupe/merge.
  //
  // Nome que não existe mais no default é lixo, não preferência: sem este filtro
  // ele sobrevive à normalização, nenhum item do painel casa com ele, e o
  // usuário fica com um cartão vazio e arrastável. Foi o que aconteceria na
  // #1243 com `contact_actions` e `contact_attributes`, que continuam gravados
  // na chave da conversa e agora moram na aba de contato.
  const knownItemNames = new Set(defaultItemsOrder.map(item => item.name));
  const knownItems = renamedItems.filter(item => knownItemNames.has(item.name));

  const dedupedItems = knownItems.filter(
    (item, index, items) =>
      items.findIndex(entry => entry.name === item.name) === index
  );

  // Seção que o usuário ainda não tem volta para o LADO DO VIZINHO, e não para
  // o fim da lista.
  //
  // Anexar no fim era mais simples e enganava: a #1243 acrescentou "Perfis
  // sociais" logo abaixo de "Dados do contato", e quem já tivesse arrastado
  // qualquer seção da aba recebia a novidade lá embaixo, depois de "Empresas
  // relacionadas" -- separada justamente da seção com que ela faz par.
  //
  // A âncora é o vizinho DE CIMA na ordem padrão que o usuário já tem. A volta
  // é para trás porque a ordem padrão é lida de cima para baixo: quando duas
  // seções novas entram seguidas, a primeira já está posicionada e vira âncora
  // da segunda, então elas não se invertem. Sem nenhuma âncora acima (a seção
  // nova é a primeira do padrão), ela entra no topo.
  defaultItemsOrder.forEach((item, defaultIndex) => {
    if (dedupedItems.find(entry => entry.name === item.name)) {
      return;
    }

    let insertAt = 0;
    for (let index = defaultIndex - 1; index >= 0; index -= 1) {
      const anchor = dedupedItems.findIndex(
        entry => entry.name === defaultItemsOrder[index].name
      );
      if (anchor !== -1) {
        insertAt = anchor + 1;
        break;
      }
    }

    dedupedItems.splice(insertAt, 0, item);
  });

  return dedupedItems;
};

/**
 * Computes the order of items in the conversation sidebar, using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
const useConversationSidebarItemsOrder = uiSettings => {
  return computed(() => {
    const { conversation_sidebar_items_order: itemsOrder } = uiSettings.value;
    return normalizeSidebarItemsOrder(
      itemsOrder,
      DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER
    );
  });
};

/**
 * Computes the order of items in the contact tab of the conversation sidebar.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
const useConversationContactItemsOrder = uiSettings => {
  return computed(() => {
    const { conversation_contact_sidebar_items_order: itemsOrder } =
      uiSettings.value;
    return normalizeSidebarItemsOrder(
      itemsOrder,
      DEFAULT_CONVERSATION_CONTACT_ITEMS_ORDER
    );
  });
};

/**
 * A ultima aba aberta do painel de detalhes, validada contra a lista de abas.
 *
 * A validacao nao e paranoia: o `ui_settings` e um JSON livre gravado por
 * qualquer versao do cliente, e um valor que nao seja aba nenhuma deixaria o
 * painel sem conteudo -- os dois blocos sao `v-if`/`v-else-if` pelo nome.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {String} A aba a abrir.
 */
const useConversationPanelActiveTab = uiSettings => {
  return computed(() => {
    const { conversation_panel_active_tab: activeTab } = uiSettings.value || {};

    return CONVERSATION_PANEL_TABS.includes(activeTab)
      ? activeTab
      : DEFAULT_CONVERSATION_PANEL_TAB;
  });
};

/**
 * Computes the order of items in the contact sidebar,using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
const useContactSidebarItemsOrder = uiSettings => {
  return computed(() => {
    const { contact_sidebar_items_order: itemsOrder } = uiSettings.value;
    return normalizeSidebarItemsOrder(
      itemsOrder,
      DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER
    );
  });
};

const normalizeBooleanSetting = (value, defaultValue = false) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }
  }

  if (value === undefined || value === null) {
    return defaultValue;
  }

  return Boolean(value);
};

/**
 * Reads a sidebar boolean from the user's UI settings.
 * @param {string} key - The settings key.
 * @param {boolean} defaultValue - Value used when the key is not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} The normalized boolean value.
 */
const readSidebarUIState = (key, defaultValue, uiSettings) => {
  const settings = uiSettings.value || {};
  return normalizeBooleanSetting(settings[key], defaultValue);
};

/**
 * Toggles the open state of a sidebar item.
 * @param {string} key - The key of the sidebar item to toggle.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const toggleSidebarUIState = (key, value, uiSettings, updateUISettings) => {
  updateUISettings({
    [key]:
      typeof value === 'boolean'
        ? value
        : !readSidebarUIState(key, false, uiSettings),
  });
};

/**
 * Sets the signature flag for a specific channel type in the inbox settings.
 * @param {string} channelType - The type of the channel.
 * @param {boolean} value - The value to set for the signature enabled flag.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const setSignatureFlagForInbox = (channelType, value, updateUISettings) => {
  if (!channelType) return;

  const slugifiedChannel = slugifyChannel(channelType);
  updateUISettings({ [`${slugifiedChannel}_signature_enabled`]: value });
};

/**
 * Fetches the signature flag for a specific channel type from UI settings.
 * @param {string} channelType - The type of the channel.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} The value of the signature enabled flag.
 */
const fetchSignatureFlagFromUISettings = (channelType, uiSettings) => {
  if (!channelType) return false;

  const slugifiedChannel = slugifyChannel(channelType);
  return uiSettings.value[`${slugifiedChannel}_signature_enabled`];
};

/**
 * Checks if a specific editor hotkey is enabled.
 * @param {string} key - The key to check.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} True if the hotkey is enabled, otherwise false.
 */
const isEditorHotKeyEnabled = (key, uiSettings) => {
  // #800: Enter passa a ser o padrao para quem nunca escolheu uma tecla.
  // `enter_to_send_enabled` e flag legada do Chatwoot que nada no app grava
  // mais (so sobrevivia aqui); com o padrao virando Enter ela perdeu efeito,
  // entao a leitura foi removida em vez de mantida morta.
  const { editor_message_key: editorMessageKey } = uiSettings.value || {};
  if (!editorMessageKey) {
    return key === 'enter';
  }
  return editorMessageKey === key;
};

/**
 * Main composable function for managing UI settings.
 * @returns {Object} An object containing reactive properties and methods for UI settings management.
 */
export function useUISettings() {
  const getters = useStoreGetters();
  const store = useStore();
  const uiSettings = computed(() => getters.getUISettings.value);

  const updateUISettings = (settings = {}) => {
    store.dispatch('updateUISettings', {
      uiSettings: {
        ...uiSettings.value,
        ...settings,
      },
    });
  };

  return {
    uiSettings,
    updateUISettings,
    conversationSidebarItemsOrder: useConversationSidebarItemsOrder(uiSettings),
    conversationContactItemsOrder: useConversationContactItemsOrder(uiSettings),
    conversationPanelActiveTab: useConversationPanelActiveTab(uiSettings),
    contactSidebarItemsOrder: useContactSidebarItemsOrder(uiSettings),
    isContactSidebarItemOpen: (key, defaultValue = false) =>
      readSidebarUIState(key, defaultValue, uiSettings),
    toggleSidebarUIState: (key, value) =>
      toggleSidebarUIState(key, value, uiSettings, updateUISettings),
    setSignatureFlagForInbox: (channelType, value) =>
      setSignatureFlagForInbox(channelType, value, updateUISettings),
    fetchSignatureFlagFromUISettings: channelType =>
      fetchSignatureFlagFromUISettings(channelType, uiSettings),
    isEditorHotKeyEnabled: key => isEditorHotKeyEnabled(key, uiSettings),
  };
}
